import { BadgePercent, Check, ChevronRight, Gift, Tag, X } from "lucide-react";
import { useEffect } from "react";
import "./MobileCartOffersSheet.css";

type MobileCartOffersSheetProps = {
    itemId: string;
    itemTitle: string;
    voucherValue: string;
    referralValue: string;
    salarCodeValue: string;
    voucherApplied: boolean;
    referralApplied: boolean;
    salarCodeApplied: boolean;
    appliedInvitationCode: string | null;
    appliedSalarCode: string | null;
    appliedVoucherAmount: number;
    onVoucherChange: (value: string) => void;
    onReferralChange: (value: string) => void;
    onSalarCodeChange: (value: string) => void;
    onApplyVoucher: () => void;
    onApplyReferral: () => void;
    onApplySalarCode: () => void;
    onRemoveReferral: () => void;
    onRemoveSalarCode: () => void;
    onClose: () => void;
};

const MobileCartOffersSheet = ({
    itemTitle,
    voucherValue,
    referralValue,
    salarCodeValue,
    voucherApplied,
    referralApplied,
    salarCodeApplied,
    appliedInvitationCode,
    appliedSalarCode,
    appliedVoucherAmount,
    onVoucherChange,
    onReferralChange,
    onSalarCodeChange,
    onApplyVoucher,
    onApplyReferral,
    onApplySalarCode,
    onRemoveReferral,
    onRemoveSalarCode,
    onClose,
}: MobileCartOffersSheetProps) => {
    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };
        window.addEventListener("keydown", handleEscape);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleEscape);
        };
    }, [onClose]);

    const isVoucherActive = voucherApplied || appliedVoucherAmount > 0;
    const isReferralActive = referralApplied || Boolean(appliedInvitationCode);
    const isSalarCodeActive = salarCodeApplied || Boolean(appliedSalarCode);

    const appliedCount = [isVoucherActive, isReferralActive, isSalarCodeActive].filter(Boolean).length;

    const hasActionableValue =
        (!isVoucherActive && voucherValue.trim() !== "") ||
        (!isReferralActive && referralValue.trim() !== "") ||
        (!isSalarCodeActive && salarCodeValue.trim() !== "");

    const handleSubmit = () => {
        if (!isVoucherActive && voucherValue.trim()) {
            onApplyVoucher();
        }
        if (!isReferralActive && referralValue.trim()) {
            onApplyReferral();
        }
        if (!isSalarCodeActive && salarCodeValue.trim()) {
            onApplySalarCode();
        }
        onClose();
    };

    const renderInput = (
        icon: React.ReactNode,
        label: string,
        helper: string,
        value: string,
        onChange: (value: string) => void,
        isActive: boolean,
        isServerApplied: boolean,
        onApply: () => void,
        onRemove: (() => void) | null,
        placeholder: string
    ) => {
        const isRemovable = isServerApplied && onRemove !== null;
        const canApply = !isActive && !isServerApplied && value.trim() !== "";
        const buttonDisabled = !isRemovable && !canApply;
        const buttonContent = isRemovable ? (
            "Remove"
        ) : isActive ? (
            <>
                <Check size={13} strokeWidth={2.3} />
                Applied
            </>
        ) : (
            "Apply"
        );

        return (
            <div className="cart-mobile-offer-group">
                <div className="cart-mobile-offer-label">
                    <span>{icon}</span>
                    <div>
                        <strong>{label}</strong>
                        <small>{helper}</small>
                    </div>
                </div>
                <div className={`cart-mobile-offer-input ${isActive ? "applied" : ""}`}>
                    <input
                        type="text"
                        value={isServerApplied ? (value ? value.toUpperCase() : "Applied") : value}
                        onChange={(event) => onChange(event.target.value)}
                        placeholder={placeholder}
                        disabled={isActive}
                    />
                    <button
                        type="button"
                        onClick={isRemovable ? onRemove : onApply}
                        disabled={buttonDisabled}
                    >
                        {buttonContent}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div
            className="cart-mobile-offer-overlay"
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <section
                className="cart-mobile-offer-sheet"
                role="dialog"
                aria-modal="true"
                aria-label="Apply offers"
            >
                <div className="cart-mobile-offer-handle" aria-hidden="true" />

                <header className="cart-mobile-offer-head">
                    <div>
                        <span>SAVE MORE</span>
                        <h2>Apply offers</h2>
                        <p>
                            Add eligible codes for <strong>{itemTitle}</strong>
                        </p>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close offers">
                        <X size={19} strokeWidth={2} />
                    </button>
                </header>

                {appliedCount > 0 && (
                    <div className="cart-mobile-offer-applied-summary">
                        <Check size={14} strokeWidth={2.4} />
                        <span>
                            {appliedCount} offer{appliedCount === 1 ? "" : "s"} applied
                        </span>
                    </div>
                )}

                <div className="cart-mobile-offer-fields">
                    {renderInput(
                        <Gift size={15} strokeWidth={1.9} />,
                        "Shopping Voucher",
                        "Apply an eligible Salar shopping voucher",
                        isVoucherActive ? voucherValue || "" : voucherValue,
                        onVoucherChange,
                        isVoucherActive,
                        isVoucherActive && appliedVoucherAmount > 0,
                        onApplyVoucher,
                        null,
                        "Enter voucher code"
                    )}

                    {renderInput(
                        <Tag size={15} strokeWidth={1.9} />,
                        "Referral ID",
                        "Add your eligible referral ID",
                        isReferralActive ? appliedInvitationCode || referralValue : referralValue,
                        onReferralChange,
                        isReferralActive,
                        isReferralActive && Boolean(appliedInvitationCode),
                        onApplyReferral,
                        onRemoveReferral,
                        "Enter referral ID"
                    )}

                    {renderInput(
                        <BadgePercent size={15} strokeWidth={1.9} />,
                        "Salar Code",
                        "Unlock an eligible Salar discount",
                        isSalarCodeActive ? appliedSalarCode || salarCodeValue : salarCodeValue,
                        onSalarCodeChange,
                        isSalarCodeActive,
                        isSalarCodeActive && Boolean(appliedSalarCode),
                        onApplySalarCode,
                        onRemoveSalarCode,
                        "Enter Salar code"
                    )}
                </div>

                <div className="cart-mobile-offer-footer">
                    <button
                        type="button"
                        className="cart-mobile-offer-submit"
                        onClick={handleSubmit}
                        disabled={!hasActionableValue}
                    >
                        Apply available offers
                        <ChevronRight size={16} strokeWidth={2.1} />
                    </button>
                    <small>Eligible discounts update automatically in your order summary.</small>
                </div>
            </section>
        </div>
    );
};

export default MobileCartOffersSheet;
